/**
 * 「已安装」判定。这是 PWA 里最反直觉、也最容易反复出事的一块。
 *
 * 三个已知失败：
 *
 * 1. 把 `fullscreen` / `minimal-ui` 也算成已安装 → 用户在普通浏览器里按 F11 全屏就被判成
 *    已安装 → 从此再也不提示添加到桌面。**只认 standalone。**
 *
 * 2. 部分安卓浏览器（尤其国产）把"添加到桌面"做成普通快捷方式，在浏览器标签里打开，
 *    `display-mode: standalone` 永远是 false → 提示反复弹，用户永远关不掉。
 *    对策是三层：多信号识别 + 一旦独立打开过就永久记住 + 用户可手动标记的逃生口。
 *
 * 3. iOS 26 起，加到主屏的站点默认就以独立 Web App 打开，安装门槛已经降低；但上面两条
 *    并没有因此失效——旧系统和第三方浏览器仍然在跑。判定逻辑不做版本分叉。
 */
import { createSafeStorage } from './storage.js';
export function readStandaloneSignals() {
    if (typeof window === 'undefined') {
        return { displayMode: false, iosStandalone: false, androidApp: false };
    }
    let displayMode = false;
    try {
        displayMode = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
    }
    catch {
        // 极老的 WebView 里 matchMedia 可能不存在或对未知媒体特性抛错
    }
    const iosStandalone = window.navigator.standalone === true;
    const androidApp = typeof document !== 'undefined' && typeof document.referrer === 'string' && document.referrer.startsWith('android-app://');
    return { displayMode, iosStandalone, androidApp };
}
/**
 * 当前这一次是否以独立（已安装）模式在运行。
 *
 * ⚠️ 这是**本次会话**的事实，不是"用户装没装过"。安卓快捷方式场景下它会是 false，
 * 判断"要不要继续提示安装"请用 {@link createInstallState}。
 */
export function isStandalone() {
    const s = readStandaloneSignals();
    return s.displayMode || s.iosStandalone || s.androidApp;
}
export function createInstallState(ns, storage = createSafeStorage()) {
    const DONE = ns.key('install.done');
    const state = {
        standalone: isStandalone,
        installed: () => isStandalone() || storage.get(DONE) === '1',
        shouldPrompt: () => !state.installed(),
        markDone: () => {
            // 存不进去（隐私模式）就降级成"本次会话内不提示"——下次可能再弹一次，可接受。
            // 反过来把提示条硬留着才是每次打开都被骚扰。
            storage.set(DONE, '1');
        },
        rememberIfStandalone: () => {
            if (isStandalone())
                storage.set(DONE, '1');
        },
        reset: () => {
            storage.remove(DONE);
        },
    };
    return state;
}
//# sourceMappingURL=standalone.js.map